import styled from '@emotion/styled'
import { memo, type ReactNode, useMemo } from 'react'

import { borderRadius } from '../../foundations/border-radius-tokens.ts'
import type { CompoundIconName } from '../../foundations/icon/index.ts'
import { type IconSize } from '../../foundations/icon/sizes.ts'
import { spacing } from '../../foundations/spacing-tokens.ts'
import { Text } from '../../foundations/text/index.ts'
import type { TextVariant } from '../../foundations/text/variant.ts'
import { Avatar } from '../avatar/avatar.tsx'
import { AvatarContext } from '../avatar/context.ts'

import { colorsPerVariant, type ChipVariant } from './chip.util.tsx'

export type ChipSize = 'small' | 'large'

export type ChipProps = {
  size: ChipSize
  /** The variant. Overriden by `backgroundColor` and `textColor` */
  variant?: ChipVariant | undefined
  dark?: boolean | undefined
  /** The background color. Overrides `variant` */
  backgroundColor?: string | undefined
  /** The text color. Overrides `variant` */
  textColor?: string | undefined

  icon?: CompoundIconName | undefined
  avatar?: ReactNode | undefined

  children: ReactNode
  className?: string | undefined
  /** When set, enables text truncation with ellipsis */
  maxWidth?: string | undefined
}

export const Chip = memo<ChipProps>(function Chip(props) {
  const { size, variant = 'neutral', dark, avatar, icon, children, className, maxWidth } = props

  const variantColors = colorsPerVariant[variant][dark ? 'dark' : 'light']
  const resolvedBackgroundColor = props.backgroundColor ?? variantColors.backgroundColor
  const resolvedTextColor = props.textColor ?? variantColors.textColor
  const iconColor = variantColors.iconColor
  const resolvedIconColor = props.textColor ?? iconColor
  const sizes = sizesPerChipSize[size]

  const setupAvatar = useMemo(
    () =>
      avatar || icon ? (
        <AvatarContext.Provider value={{ size: sizes.avatarSize }}>
          {avatar || <Avatar icon={icon} size={sizes.avatarSize} color={resolvedIconColor} />}
        </AvatarContext.Provider>
      ) : null,
    [avatar, icon, sizes, resolvedIconColor],
  )

  return (
    <ChipContainer
      className={className}
      resolvedBackgroundColor={resolvedBackgroundColor}
      sizes={sizes}
      maxWidth={maxWidth}
    >
      {avatar || icon ? setupAvatar : null}
      {children != null ? (
        <TextWithEllipsis variant={sizes?.textVariant} resolvedTextColor={resolvedTextColor}>
          {children}
        </TextWithEllipsis>
      ) : null}
    </ChipContainer>
  )
})
const ChipContainer = styled.div<{
  resolvedBackgroundColor: string
  sizes: Sizes
  maxWidth?: string | undefined
}>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[30]};
  max-width: ${({ maxWidth }) => maxWidth ?? 'min-content'};
  ${({ maxWidth }) => (maxWidth ? 'min-width: 0;' : '')}
  ${({ maxWidth }) => (maxWidth ? 'overflow: hidden;' : '')}

  padding: ${spacing[20]} ${spacing[30]};

  background-color: ${({ resolvedBackgroundColor }) => resolvedBackgroundColor};
  border-radius: ${borderRadius[50]};
`

const TextWithEllipsis = styled(Text)<{ resolvedTextColor: string }>`
  color: ${({ resolvedTextColor }) => resolvedTextColor};

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

type Sizes = {
  height: string
  avatarSize: IconSize
  textVariant: TextVariant
}

const sizesPerChipSize: {
  [size in ChipSize]: Sizes
} = {
  small: { height: '24px', avatarSize: '10', textVariant: 'caption' },
  large: { height: '28px', avatarSize: '30', textVariant: 'body2' },
}
